/**
 * TwoFactorSettings — Profile → Security card for TOTP 2FA + backup codes.
 * Enable flow: setup (QR) → confirm 6-digit code → one-time backup codes.
 * Enabled state: codes remaining, regenerate codes, disable.
 */
import { useEffect, useState } from 'react';
import { ShieldCheck, ShieldOff, KeyRound, Copy, Download, RefreshCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';

const API = process.env.REACT_APP_BACKEND_URL;

async function api(path, options = {}) {
  const res = await fetch(`${API}/api/auth/2fa${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Request failed');
  return data;
}

export const TwoFactorSettings = () => {
  const [status, setStatus] = useState(null);
  const [setup, setSetup] = useState(null);          // {qr_png_b64, secret}
  const [confirmCode, setConfirmCode] = useState('');
  const [backupCodes, setBackupCodes] = useState(null);
  const [actionCode, setActionCode] = useState('');
  const [showDisable, setShowDisable] = useState(false);
  const [showRegen, setShowRegen] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = () => api('/status').then(setStatus).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const startSetup = async () => {
    setBusy(true);
    try { setSetup(await api('/setup', { method: 'POST' })); }
    catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const confirmEnable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const d = await api('/enable', { method: 'POST', body: JSON.stringify({ code: confirmCode }) });
      setBackupCodes(d.backup_codes || []);
      setSetup(null);
      setConfirmCode('');
      toast.success('Two-factor authentication is now active.');
      refresh();
    } catch (e2) { toast.error(e2.message); }
    finally { setBusy(false); }
  };

  const disable2fa = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api('/disable', { method: 'POST', body: JSON.stringify({ code: actionCode }) });
      toast.success('Two-factor authentication turned off.');
      setShowDisable(false);
      setActionCode('');
      setBackupCodes(null);
      refresh();
    } catch (e2) { toast.error(e2.message); }
    finally { setBusy(false); }
  };

  const regenCodes = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const d = await api('/backup-codes/regenerate', { method: 'POST', body: JSON.stringify({ code: actionCode }) });
      setBackupCodes(d.backup_codes || []);
      setShowRegen(false);
      setActionCode('');
      toast.success('New backup codes generated — previous codes are void.');
      refresh();
    } catch (e2) { toast.error(e2.message); }
    finally { setBusy(false); }
  };

  const copyCodes = () => {
    navigator.clipboard?.writeText((backupCodes || []).join('\n'))
      .then(() => toast.success('Backup codes copied'))
      .catch(() => toast.error('Copy failed — select and copy manually'));
  };

  const downloadCodes = () => {
    const blob = new Blob(
      [`Noble Empire NECS — 2FA Backup Codes\nEach code works exactly once. Keep them safe.\n\n${(backupCodes || []).join('\n')}\n`],
      { type: 'text/plain' }
    );
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'necs-2fa-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (!status) return null;

  return (
    <div
      className="rounded-xl p-5"
      style={{ backgroundColor: '#ffffff', border: '1px solid rgba(0,0,0,0.12)' }}
      data-testid="twofa-settings-card"
    >
      <div className="flex items-center gap-2 mb-1">
        {status.enabled
          ? <ShieldCheck className="h-5 w-5" style={{ color: '#10b981' }} />
          : <ShieldOff className="h-5 w-5" style={{ color: '#C9A962' }} />}
        <p className="font-bold text-sm uppercase tracking-wider" style={{ color: '#000' }}>
          Two-Factor Authentication (2FA)
        </p>
        <span
          className="ml-auto text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full"
          style={status.enabled
            ? { backgroundColor: 'rgba(16,185,129,0.12)', color: '#059669' }
            : { backgroundColor: 'rgba(0,0,0,0.06)', color: '#666' }}
          data-testid="twofa-status-badge"
        >
          {status.enabled ? 'Enabled' : 'Off'}
        </span>
      </div>
      <p className="text-xs mb-4" style={{ color: '#555' }}>
        Add a second lock to your account with an authenticator app (Google Authenticator, Authy, 1Password).
        You&apos;ll also get one-time backup codes so you never lock yourself out.
      </p>

      {/* ── Disabled state: enable flow ── */}
      {!status.enabled && !setup && !backupCodes && (
        <Button
          type="button"
          onClick={startSetup}
          disabled={busy}
          className="font-bold text-xs"
          style={{ backgroundColor: '#000', color: '#C9A962', border: '1px solid #C9A962' }}
          data-testid="twofa-enable-btn"
        >
          <ShieldCheck className="h-4 w-4 mr-1.5" /> Enable 2FA
        </Button>
      )}

      {setup && (
        <form onSubmit={confirmEnable} className="space-y-3" data-testid="twofa-setup-block">
          <p className="text-xs font-semibold" style={{ color: '#000' }}>
            1 · Scan this QR code with your authenticator app:
          </p>
          <img
            src={`data:image/png;base64,${setup.qr_png_b64}`}
            alt="2FA QR code"
            className="w-40 h-40 rounded-lg border"
            style={{ borderColor: 'rgba(0,0,0,0.15)' }}
            data-testid="twofa-qr-image"
          />
          <p className="text-[11px]" style={{ color: '#666' }}>
            Can&apos;t scan? Enter this key manually: <code className="font-bold">{setup.secret}</code>
          </p>
          <p className="text-xs font-semibold" style={{ color: '#000' }}>2 · Enter the 6-digit code to confirm:</p>
          <div className="flex gap-2 max-w-xs">
            <Input
              value={confirmCode}
              onChange={(e) => setConfirmCode(e.target.value)}
              placeholder="123456"
              maxLength={6}
              inputMode="numeric"
              required
              data-testid="twofa-confirm-code-input"
            />
            <Button
              type="submit"
              disabled={busy || confirmCode.length !== 6}
              className="font-bold text-xs"
              style={{ backgroundColor: '#C9A962', color: '#1a1a1a' }}
              data-testid="twofa-confirm-enable-btn"
            >
              Verify & Enable
            </Button>
          </div>
          <button
            type="button"
            onClick={() => { setSetup(null); setConfirmCode(''); }}
            className="text-[11px] underline"
            style={{ color: '#888' }}
            data-testid="twofa-cancel-setup"
          >
            Cancel setup
          </button>
        </form>
      )}

      {/* ── Backup codes reveal (shown ONCE after enable / regenerate) ── */}
      {backupCodes && (
        <div
          className="mt-3 rounded-lg p-4"
          style={{ backgroundColor: 'rgba(201,169,98,0.08)', border: '1px dashed #C9A962' }}
          data-testid="twofa-backup-codes-panel"
        >
          <p className="text-xs font-black uppercase tracking-wider mb-1" style={{ color: '#000' }}>
            <KeyRound className="h-3.5 w-3.5 inline mr-1" style={{ color: '#C9A962' }} />
            Your one-time backup codes
          </p>
          <p className="text-[11px] mb-3" style={{ color: '#8a6d1a' }}>
            Save these now — they are shown only once. Each code signs you in exactly one time if you lose your authenticator.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 mb-3">
            {backupCodes.map((c) => (
              <code key={c} className="text-[11px] font-bold px-2 py-1 rounded text-center" style={{ backgroundColor: '#fff', border: '1px solid rgba(0,0,0,0.12)', color: '#000' }}>
                {c}
              </code>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" onClick={copyCodes} className="text-[11px] font-bold h-8" style={{ backgroundColor: '#000', color: '#C9A962' }} data-testid="twofa-copy-codes-btn">
              <Copy className="h-3.5 w-3.5 mr-1" /> Copy All
            </Button>
            <Button type="button" onClick={downloadCodes} className="text-[11px] font-bold h-8" style={{ backgroundColor: '#000', color: '#C9A962' }} data-testid="twofa-download-codes-btn">
              <Download className="h-3.5 w-3.5 mr-1" /> Download .txt
            </Button>
            <Button type="button" onClick={() => setBackupCodes(null)} className="text-[11px] font-bold h-8" style={{ backgroundColor: '#C9A962', color: '#1a1a1a' }} data-testid="twofa-codes-saved-btn">
              I&apos;ve saved my codes
            </Button>
          </div>
        </div>
      )}

      {/* ── Enabled state: manage ── */}
      {status.enabled && !backupCodes && (
        <div className="space-y-3">
          <p className="text-xs" style={{ color: '#333' }} data-testid="twofa-codes-remaining">
            <KeyRound className="h-3.5 w-3.5 inline mr-1" style={{ color: '#C9A962' }} />
            Backup codes remaining: <b>{status.backup_codes_remaining}</b>
            {status.backup_codes_remaining <= 2 && (
              <span className="ml-2 font-bold" style={{ color: '#c62828' }}>— running low, regenerate soon</span>
            )}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button type="button" onClick={() => { setShowRegen((v) => !v); setShowDisable(false); setActionCode(''); }}
                    className="text-[11px] font-bold h-8" style={{ backgroundColor: '#000', color: '#C9A962', border: '1px solid #C9A962' }}
                    data-testid="twofa-regen-toggle">
              <RefreshCcw className="h-3.5 w-3.5 mr-1" /> Regenerate Backup Codes
            </Button>
            <Button type="button" onClick={() => { setShowDisable((v) => !v); setShowRegen(false); setActionCode(''); }}
                    className="text-[11px] font-bold h-8" style={{ backgroundColor: '#fff', color: '#c62828', border: '1px solid #c62828' }}
                    data-testid="twofa-disable-toggle">
              <ShieldOff className="h-3.5 w-3.5 mr-1" /> Disable 2FA
            </Button>
          </div>

          {(showRegen || showDisable) && (
            <form onSubmit={showRegen ? regenCodes : disable2fa} className="flex gap-2 max-w-sm items-center" data-testid="twofa-action-form">
              <Input
                value={actionCode}
                onChange={(e) => setActionCode(e.target.value)}
                placeholder={showRegen ? 'Authenticator code' : 'Code or backup code'}
                required
                data-testid="twofa-action-code-input"
              />
              <Button type="submit" disabled={busy || !actionCode}
                      className="text-[11px] font-bold h-9 whitespace-nowrap"
                      style={showRegen
                        ? { backgroundColor: '#C9A962', color: '#1a1a1a' }
                        : { backgroundColor: '#c62828', color: '#fff' }}
                      data-testid="twofa-action-submit">
                {showRegen ? 'Generate' : 'Turn Off'}
              </Button>
            </form>
          )}
        </div>
      )}
    </div>
  );
};

export default TwoFactorSettings;
